import { zodResolver } from '@hookform/resolvers/zod';
import { motion, useReducedMotion } from 'framer-motion';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { fieldStaggerVariants, fieldVariants, shakeVariants } from '@/shared/lib';
import { Alert, AlertDescription, AlertTitle, Button, Input, Label, Textarea } from '@/shared/ui';

import type {
  ActualizarCatalogoItemInput,
  CrearCatalogoItemInput,
} from '../model/catalogo-proyecto.schemas';

type Mode = 'crear' | 'editar';

type CommonProps = {
  mode: Mode;
  submitLabel: string;
  cancelLabel?: string;
  onCancel?: () => void;
};

type CrearProps = CommonProps & {
  mode: 'crear';
  onSubmit: (values: CrearCatalogoItemInput) => Promise<unknown>;
  defaultValues?: Partial<CrearCatalogoItemInput>;
};

type EditarProps = CommonProps & {
  mode: 'editar';
  onSubmit: (values: ActualizarCatalogoItemInput) => Promise<unknown>;
  defaultValues: ActualizarCatalogoItemInput;
};

export type CatalogoItemFormProps = CrearProps | EditarProps;

/**
 * Schema interno del form. Mantiene `descripcion` como string (default
 * '') para que RHF lo trate como controlado siempre. Al hacer submit
 * se normaliza a `undefined`/`null` según el modo del caller.
 */
const formSchema = z.object({
  nombre: z.string().trim().min(1, 'El nombre es obligatorio.').max(120, 'Máximo 120 caracteres.'),
  descripcion: z.string().trim().max(500, 'Máximo 500 caracteres.'),
});

type FormValues = z.infer<typeof formSchema>;

/**
 * Formulario compartido entre crear y editar item de catálogo. Los 2
 * campos editables (`nombre`, `descripcion`) son los únicos del shape;
 * `activo`, `creadoEn`, `actualizadoEn` los administra el backend.
 *
 * `mode` decide qué schema Zod aplicar:
 *   - `crear`: nombre es obligatorio.
 *   - `editar`: ambos opcionales (PATCH parcial); `descripcion: null`
 *     se interpreta como "limpiar el campo" (contrato backend).
 */
export function CatalogoItemForm(props: CatalogoItemFormProps) {
  const { mode, submitLabel, cancelLabel, onCancel } = props;
  const reducedMotion = useReducedMotion();

  const [inlineError, setInlineError] = useState<{ title: string; description: string } | null>(
    null,
  );
  const [shakeKey, setShakeKey] = useState(0);

  const nombreRef = useRef<HTMLInputElement | null>(null);

  const initialValues: FormValues =
    mode === 'crear'
      ? {
          nombre: props.defaultValues?.nombre ?? '',
          descripcion: props.defaultValues?.descripcion ?? '',
        }
      : {
          nombre: props.defaultValues.nombre ?? '',
          descripcion: props.defaultValues.descripcion ?? '',
        };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  const { ref: nombreRhfRef, ...nombreRhfRest } = register('nombre');

  const submit = handleSubmit(async (values) => {
    setInlineError(null);
    try {
      if (mode === 'crear') {
        const payload: CrearCatalogoItemInput = { nombre: values.nombre };
        if (values.descripcion) payload.descripcion = values.descripcion;
        await props.onSubmit(payload);
      } else {
        // PATCH: cadena vacía se traduce a `null` para que el backend
        // limpie el campo (contrato `UpdateCatalogoProyectoItemDto`).
        const payload: ActualizarCatalogoItemInput = {
          nombre: values.nombre,
          descripcion: values.descripcion ? values.descripcion : null,
        };
        await props.onSubmit(payload);
      }
    } catch (e) {
      const apiCode = readApiCode(e);
      const view = mapInlineError(apiCode, mode);
      setInlineError(view);
      setShakeKey((k) => k + 1);
      window.setTimeout(() => nombreRef.current?.focus(), 0);
    }
  });

  return (
    <motion.form
      onSubmit={(e) => void submit(e)}
      variants={fieldStaggerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-4"
      noValidate
    >
      {inlineError && (
        <motion.div
          key={shakeKey}
          {...(reducedMotion ? {} : { variants: shakeVariants, animate: 'shake' })}
          role="alert"
          aria-live="polite"
        >
          <Alert variant="destructive">
            <AlertTitle>{inlineError.title}</AlertTitle>
            <AlertDescription>{inlineError.description}</AlertDescription>
          </Alert>
        </motion.div>
      )}

      <motion.div variants={fieldVariants} className="space-y-1.5">
        <Label htmlFor="catalogo-nombre">Nombre</Label>
        <Input
          id="catalogo-nombre"
          autoComplete="off"
          maxLength={120}
          ref={(node) => {
            nombreRhfRef(node);
            nombreRef.current = node;
          }}
          aria-invalid={Boolean(errors.nombre)}
          aria-describedby={errors.nombre ? 'catalogo-nombre-error' : undefined}
          {...nombreRhfRest}
        />
        {errors.nombre && (
          <p id="catalogo-nombre-error" className="text-sm text-destructive">
            {errors.nombre.message}
          </p>
        )}
      </motion.div>

      <motion.div variants={fieldVariants} className="space-y-1.5">
        <Label htmlFor="catalogo-descripcion">Descripción (opcional)</Label>
        <Textarea
          id="catalogo-descripcion"
          rows={3}
          maxLength={500}
          placeholder="Detalle breve para que otros usuarios entiendan el uso del catálogo."
          aria-invalid={Boolean(errors.descripcion)}
          aria-describedby={errors.descripcion ? 'catalogo-descripcion-error' : undefined}
          {...register('descripcion')}
        />
        {errors.descripcion && (
          <p id="catalogo-descripcion-error" className="text-sm text-destructive">
            {errors.descripcion.message}
          </p>
        )}
      </motion.div>

      <motion.div
        variants={fieldVariants}
        className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:gap-3 sm:[&>*]:flex-1"
      >
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            {cancelLabel ?? 'Cancelar'}
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {submitLabel}
        </Button>
      </motion.div>
    </motion.form>
  );
}

function readApiCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

function mapInlineError(
  code: string | undefined,
  mode: Mode,
): { title: string; description: string } {
  switch (code) {
    case 'CATALOGO_PROYECTO_NOMBRE_DUPLICADO':
      return {
        title: 'Ya existe un item con este nombre',
        description: 'Pruebe con otro nombre — el catálogo no admite duplicados.',
      };
    case 'CATALOGO_PROYECTO_ITEM_NOT_FOUND':
      return {
        title: 'No encontramos el item',
        description: 'Pudo haber sido eliminado por otro usuario. Vuelva al listado.',
      };
    case 'CATALOGO_PROYECTO_ESTADO_INVALIDO':
      return {
        title: 'Estado inválido',
        description: 'El item ya está en el estado que intenta aplicar.',
      };
    case 'VALIDATION_ERROR':
      return {
        title: 'Revise los datos',
        description: 'Algunos campos no son válidos. Verifique el nombre y la descripción.',
      };
    default:
      return {
        title: mode === 'crear' ? 'No pudimos crear el item' : 'No pudimos guardar los cambios',
        description: 'Ocurrió un error inesperado. Vuelva a intentar en unos segundos.',
      };
  }
}
